<template>
  <section :class="framed ? 'rounded-md border border-land-border bg-land-surface shadow-sm' : ''">
    <div class="flex items-center justify-between gap-3 border-land-border-soft" :class="framed ? 'border-b p-4' : 'pb-3'">
      <div class="min-w-0">
        <h2 class="text-lg font-semibold tracking-normal">{{ title }}</h2>
        <p class="mt-1 text-xs text-land-ink-soft">{{ images.length }} images</p>
      </div>
      <button type="button" class="inline-flex h-9 items-center gap-2 rounded-md border border-land-border bg-white px-3 text-sm font-medium text-land-ink-muted hover:bg-land-surface-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-land-primary disabled:cursor-wait disabled:opacity-60" :disabled="isUploading" :aria-label="`Add ${title.toLowerCase()}`" @click="openFilePicker">
        <LoaderCircle v-if="isUploading" class="size-4 animate-spin" aria-hidden="true" />
        <ImagePlus v-else class="size-4" aria-hidden="true" />
        Add image
      </button>
      <input ref="fileInput" class="sr-only" type="file" accept="image/*" multiple :disabled="isUploading" tabindex="-1" @change="handleFileInput">
    </div>

    <div
      :class="[
        'relative min-h-40 bg-land-surface-muted/45 p-3 transition',
        framed ? 'border-b border-land-border-soft' : 'rounded-md border border-land-border-soft',
        isDragging ? 'bg-land-primary-soft ring-2 ring-inset ring-land-primary/30' : ''
      ]"
      @dragenter.prevent="isDragging = true"
      @dragover.prevent="isDragging = true"
      @dragleave.prevent="isDragging = false"
      @drop.prevent="handleDrop"
    >
      <div v-if="images.length > 0" class="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <article v-for="image in images" :key="image.id" class="group relative overflow-hidden rounded-md border border-land-border bg-white">
          <a :href="image.url" target="_blank" rel="noreferrer" class="block aspect-[4/3] bg-land-surface-soft">
            <img :src="image.url" :alt="image.filename" class="h-full w-full object-cover">
          </a>
          <div class="flex items-center justify-between gap-2 px-2 py-2">
            <p class="min-w-0 truncate text-xs font-medium text-land-ink-muted">{{ image.filename }}</p>
            <button type="button" class="grid size-7 shrink-0 place-items-center rounded-md text-land-ink-soft hover:bg-red-50 hover:text-red-700 disabled:cursor-wait disabled:opacity-60" :disabled="deletingIds.includes(image.id)" :aria-label="`Delete ${image.filename}`" @click="deleteImage(image)">
              <LoaderCircle v-if="deletingIds.includes(image.id)" class="size-3.5 animate-spin" aria-hidden="true" />
              <Trash2 v-else class="size-3.5" aria-hidden="true" />
            </button>
          </div>
        </article>
      </div>

      <div v-else class="grid min-h-32 place-items-center rounded-md border border-dashed border-land-border bg-white/70 text-center">
        <div>
          <Image class="mx-auto size-8 text-land-ink-soft" aria-hidden="true" />
          <p class="mt-2 text-sm font-medium text-land-ink-muted">No images yet</p>
        </div>
      </div>

      <div v-if="isDragging" class="pointer-events-none absolute inset-0 grid place-items-center bg-land-primary-soft/80 text-sm font-semibold text-land-primary">
        Drop to upload
      </div>
    </div>

    <div v-if="errors.length > 0" class="space-y-1 p-3 text-sm text-red-700">
      <p v-for="error in errors" :key="error">{{ error }}</p>
    </div>
  </section>
</template>

<script setup>
import { Image, ImagePlus, LoaderCircle, Trash2 } from '@lucide/vue'
import { ref } from 'vue'
import { normalizeErrors, requestJson } from '@/api/client'

const props = defineProps({
  title: {
    type: String,
    required: true
  },
  uploadUrl: {
    type: String,
    required: true
  },
  images: {
    type: Array,
    default: () => []
  },
  framed: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits(['updated'])

const fileInput = ref(null)
const isDragging = ref(false)
const isUploading = ref(false)
const deletingIds = ref([])
const errors = ref([])

const openFilePicker = () => {
  if (isUploading.value || !fileInput.value) return

  try {
    if (typeof fileInput.value.showPicker === 'function') {
      fileInput.value.showPicker()
    } else {
      fileInput.value.click()
    }
  } catch {
    fileInput.value.click()
  }
}

const handleFileInput = async (event) => {
  await uploadFiles(event.target.files)
  event.target.value = ''
}

const handleDrop = async (event) => {
  isDragging.value = false
  await uploadFiles(event.dataTransfer.files)
}

const uploadFiles = async (fileList) => {
  const files = Array.from(fileList || []).filter((file) => file.type.startsWith('image/'))
  if (files.length === 0) return

  const formData = new FormData()
  files.forEach((file) => formData.append('images[]', file))
  isUploading.value = true
  errors.value = []

  try {
    const data = await requestJson(props.uploadUrl, {
      method: 'POST',
      body: formData
    })

    emit('updated', data.images || [])
  } catch (error) {
    errors.value = normalizeErrors(error)
  } finally {
    isUploading.value = false
  }
}

const deleteImage = async (image) => {
  deletingIds.value = [...deletingIds.value, image.id]
  errors.value = []

  try {
    const data = await requestJson(`${props.uploadUrl}/${image.id}`, {
      method: 'DELETE'
    })

    emit('updated', data.images || [])
  } catch (error) {
    errors.value = normalizeErrors(error)
  } finally {
    deletingIds.value = deletingIds.value.filter((id) => id !== image.id)
  }
}
</script>
